import React, { useState } from 'react';
import {
  Paper,
  Typography,
  IconButton,
  Collapse,
  Box,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HealthFilesGrid from './HealthFilesGrid';

function hasGroupValue(file, field) {
  const val = file[field];
  if (val == null) return false;
  if (typeof val === 'string' && !val.trim()) return false;
  return true;
}

function buildGroups(files, field) {
  const groups = {};
  for (let i = 0; i < files.length; i += 1) {
    const f = files[i];
    if (!hasGroupValue(f, field)) continue;
    const key = String(f[field]).trim();
    if (!groups[key]) groups[key] = [];
    groups[key].push(f);
  }
  const keys = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  return { groups, keys };
}

export default function HealthGroupedView({
  files,
  fieldOptions,
  request,
  onFileUpdate,
  onFileDelete,
  onPreview,
  downloadMode,
  downloadSelected,
  onToggleDownloadSelect,
}) {
  const [groupTab, setGroupTab] = useState('report_type');
  const [expanded, setExpanded] = useState({});

  const field = groupTab === 'type' ? 'type' : 'report_type';
  const { groups, keys } = buildGroups(files, field);

  function toggle(key) {
    setExpanded((e) => ({ ...e, [key]: !e[key] }));
  }

  if (files.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No files match your filters.</Typography>
      </Paper>
    );
  }

  if (keys.length === 0) {
    return (
      <Stack spacing={2}>
        <Tabs
          value={groupTab}
          onChange={(_, v) => {
            setGroupTab(v);
            setExpanded({});
          }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="report_type" label="By report type" />
          <Tab value="type" label="By type" />
        </Tabs>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No files with a {groupTab === 'type' ? 'type' : 'report type'} set match your filters.
          </Typography>
        </Paper>
      </Stack>
    );
  }

  const sections = [];
  for (let i = 0; i < keys.length; i += 1) {
    const groupKey = keys[i];
    const rows = groups[groupKey];
    const isOpen = expanded[groupKey] !== false;
    sections.push(
      <Paper key={groupKey} variant="outlined">
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            bgcolor: 'action.hover',
          }}
          onClick={() => toggle(groupKey)}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {groupKey} ({rows.length})
          </Typography>
          <IconButton size="small" aria-label={isOpen ? 'Collapse' : 'Expand'}>
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={isOpen}>
          <Box sx={{ p: 1 }}>
            <HealthFilesGrid
              files={rows}
              fieldOptions={fieldOptions}
              request={request}
              onFileUpdate={onFileUpdate}
              onFileDelete={onFileDelete}
              onPreview={onPreview}
              downloadMode={downloadMode}
              downloadSelected={downloadSelected}
              onToggleDownloadSelect={onToggleDownloadSelect}
            />
          </Box>
        </Collapse>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <Tabs
        value={groupTab}
        onChange={(_, v) => {
          setGroupTab(v);
          setExpanded({});
        }}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="report_type" label="By report type" />
        <Tab value="type" label="By type" />
      </Tabs>
      <Stack spacing={1}>{sections}</Stack>
    </Stack>
  );
}
